import React from "react";
import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";

const pulse = keyframes`
  0%,
  100% {
    transform: scale(0.82);
    opacity: 0.42;
  }

  50% {
    transform: scale(1);
    opacity: 1;
  }
`;

export default function Q360LoadingState({
  message = "Chargement en cours…",
  description,
  compact = false,
  className,
}) {
  return (
    <LoadingState
      role="status"
      aria-live="polite"
      aria-busy="true"
      $compact={compact}
      className={className}
      data-density={compact ? "compact" : "default"}
    >
      <LoadingIndicator aria-hidden="true">
        <LoadingDot $delay="0s" />
        <LoadingDot $delay="0.16s" />
        <LoadingDot $delay="0.32s" />
      </LoadingIndicator>

      <LoadingCopy>
        <LoadingMessage>{message}</LoadingMessage>

        {description && (
          <LoadingDescription>
            {description}
          </LoadingDescription>
        )}
      </LoadingCopy>
    </LoadingState>
  );
}

Q360LoadingState.propTypes = {
  message: PropTypes.string,
  description: PropTypes.string,
  compact: PropTypes.bool,
  className: PropTypes.string,
};

const LoadingState = styled.section`
  min-height: ${({ $compact }) => ($compact ? "52px" : "112px")};
  padding: ${({ $compact }) =>
    $compact
      ? "var(--q360-space-sm, 10px) var(--q360-space-sm, 10px)"
      : "1.1rem"};
  border: 1px solid var(
    --q360-border-subtle,
    rgba(255, 255, 255, 0.08)
  );
  border-radius: ${({ $compact }) =>
    $compact
      ? "var(--q360-radius-sm, 10px)"
      : "18px"};
  background: rgba(255, 255, 255, 0.045);
  color: var(--q360-text-primary, #f7f7f7);

  display: flex;
  flex-direction: ${({ $compact }) =>
    $compact ? "row" : "column"};
  align-items: center;
  justify-content: center;
  gap: ${({ $compact }) => ($compact ? "0.7rem" : "0.85rem")};

  text-align: center;
`;

const LoadingIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.34rem;
`;

const LoadingDot = styled.span`
  width: 0.58rem;
  height: 0.58rem;
  border-radius: 999px;
  background: var(--q360-text-primary, #fff9ea);
  animation: ${pulse} 1.15s infinite ease-in-out;
  animation-delay: ${({ $delay }) => $delay};
`;

const LoadingCopy = styled.span`
  display: grid;
  gap: 0.25rem;
  min-width: 0;
`;

const LoadingMessage = styled.span`
  color: var(
    --q360-text-muted,
    rgba(247, 247, 247, 0.78)
  );
  font-size: 0.9rem;
  font-weight: 750;
  line-height: 1.4;
`;

const LoadingDescription = styled.span`
  color: var(
    --q360-text-muted,
    rgba(247, 247, 247, 0.68)
  );
  font-size: 0.82rem;
  line-height: 1.4;
`;
